import { produce } from 'immer';

export const SET_DATA = 'SET_DATA';
export const FORM_SET_FIELD = 'FORM_SET_FIELD';
export const FORM_SUBMIT = 'FORM_SUBMIT';
export const FORM_CLEAR = 'FORM_CLEAR';

const UPCASE_FIRST_LETTER_REGEX = /(^\w|\s\w)/g;

export const initialState = {
  data: {
    loaded: false,
    lawyers: null,
    firms: [],
    regions: {},
    cities: [],
    filteredCities: [],
    typesOfCase: {},
    subcategories: [],
  },
  form: {
    name: null,
    firm: null,
    region: null,
    city: null,
    typeOfCase: null,
    subcategory: null,
  },
  submitted: false,
  results: [],
};

export default function reducer(state: object, action) {
  const { type, payload } = action;

  const nextState = produce(state, (draft) => {
    switch (type) {
      case SET_DATA:
        // Generate dropdown field values
        draft.data.lawyers = payload
          .filter((row) => row.name) // Remove any rows without name
          .map((row) => {
            draft.data.firms.push(row.firm);

            // Although there is a copy of cities under each region, we still need
            // a standalone list for display in cities dropdown when no region is
            // selected.
            draft.data.cities.push(row.city);

            if (row.region) {
              // Make region case consistent to fix data issues, e.g., Bay of Plenty vs Bay Of Plenty.
              const key = row.region.replace(UPCASE_FIRST_LETTER_REGEX, (m) =>
                m.toUpperCase(),
              );

              // region and city are dependent, so nest
              if (!draft.data.regions[key]) {
                draft.data.regions[key] = [];
              }

              if (row.city) {
                draft.data.regions[key].push(row.city);
              }
            }

            // The spreadsheet from which we get data does not have typeOfCase set when the subcategory
            // is 'Family Legal Advice', this means this subcategory will not show up. To fix this we
            // set the typeOfCase to 'Famil' for these rows.
            // MOJ have indicated they cannot fix this data issue as the export is automatically generated
            // and is also used by other systems.
            if (row.subcategory === 'Family Legal Advice') {
              row.typeOfCase = 'Family';
            }

            // There is some variation in the PAL subcategories, e.g. sometimes this is 'PAL 1' and other times
            // 'PAL1'. We remove space here to add consistency.
            if (row.subcategory.includes('PAL')) {
              row.subcategory = row.subcategory.replace(
                /^(.+)\s([0-9])$/g,
                '$1$2',
              );
            }

            if (row.typeOfCase) {
              // typeOfCase and subcategory are dependent, so nest
              if (!draft.data.typesOfCase[row.typeOfCase]) {
                draft.data.typesOfCase[row.typeOfCase] = [];
              }

              if (row.subcategory) {
                draft.data.typesOfCase[row.typeOfCase].push(row.subcategory);
              }
            }

            // Add surname to data
            return {
              ...row,
            };
          });

        // Remove duplicates, empty values, and sort
        const cleanse = (values) => {
          const cleanValues = values
            .filter((value) => !!value)
            .map((value) =>
              value.replace(UPCASE_FIRST_LETTER_REGEX, (m) => m.toUpperCase()),
            )
            .sort();

          return [...new Set(cleanValues)];
        };
        draft.data = {
          ...draft.data,
          firms: cleanse(draft.data.firms),
          cities: cleanse(draft.data.cities),
          loaded: true,
        };

        break;

      case FORM_SET_FIELD:
        const { name, value } = payload;

        // If the value is the default 'Any' treat this as null so it can be ignored during search
        draft.form[name] = value === 'Any' ? null : value;

        // Sort subcategories and set subcategory to null so results are no affected by previously
        // set value.
        if (name === 'typeOfCase') {
          draft.data.subcategories = [
            ...new Set(draft.data.typesOfCase[value]),
          ].sort();

          draft.form.subcategory = null;
        }

        // Sort cities and set city to null so results are no affected by previously set value.
        if (name === 'region') {
          draft.data.filteredCities = [
            ...new Set(draft.data.regions[value]),
          ].sort();

          draft.form.city = null;
        }

        break;

      case FORM_SUBMIT:
        const uniqResults = {};

        const form = state.form;

        // Iterate through all rows, to aggregate lawyer data and search until we either get a hit
        // on a row corresponding to a lawyer or run out of rows.
        draft.data.lawyers.forEach((lawyer) => {
          const { searchResults, subcategory, typeOfCase, ...other } = lawyer;

          // Generate a unique string of values shared between rows for the same lawyer, this
          // will allow us to remove duplicate rows, but concatenate searchResults, subcategories,
          // and typesOfCase from all rows pertaining to the same lawyer.
          // uniqResults[hash].hit will be used to mark whether a row associated with a lawyer matched
          // the search.
          const hash = Object.values(other)
            .join('')
            .replace(/[^a-z0-9]/gi, '');

          if (!(hash in uniqResults)) {
            uniqResults[hash] = {
              ...other,
              searchResults: [],
              subcategories: [],
              typesOfCase: [],
            };
          }

          // If we have had a hit on a previous row for the same lawyer, short-circuit
          // and do not search further rows.
          if (!uniqResults[hash].hit) {
            let hit = true;

            for (const field in form) {
              // If the form field does not have a value, we cannot search on this field.
              if (!form[field]) {
                continue;
              }

              // If the lawyer does not have a value in a specific field, this row is not a hit.
              if (!lawyer[field]) {
                hit = false;
              } else {
                // Determine whether the value in this row matches the value in the form field.
                const lawyerVal = lawyer[field].toLowerCase();
                const formVal = form[field].toLowerCase().trim();

                // For typeOfCase and subcategory values we want an exact match.
                if (['typeOfCase', 'subcategory'].includes(field)) {
                  if (lawyerVal !== formVal) {
                    hit = false;
                  }
                } else {
                  if (!lawyerVal.includes(formVal)) {
                    hit = false;
                  }
                }
              }

              // If we have not got a hit, short-circuit and do not check further fields against this row.
              if (!hit) {
                break;
              }
            }

            uniqResults[hash].hit = hit;
          }

          // Some values for a lawyer are spread across multiple rows, so we aggregate these into
          // a single property on a uniqResults entry.
          const toAggregate = {
            searchResults,
            subcategories: subcategory,
            typesOfCase: typeOfCase,
          };
          for (const key in toAggregate) {
            if (
              toAggregate[key] &&
              !uniqResults[hash][key].includes(toAggregate[key])
            ) {
              uniqResults[hash][key].push(toAggregate[key]);
            }
          }
        });

        const results = [];

        // Convert uniqResults to an array of objects, we keep the unique key so this can be
        // used as the 'key' prop on table rows.
        // Only add a lawyer from uniqResults if hit === true.
        for (const key in uniqResults) {
          // Prevent row values set being added to
          if (uniqResults[key].hit) {
            results.push({
              ...uniqResults[key],
              searchResults: uniqResults[key].searchResults.sort(),
              key,
            });
          }
        }

        // Sort results by city, then name, then update state
        draft.results = results.sort((a, b) => {
          if (a.city === b.city) {
            return a.name < b.name ? -1 : 1;
          } else {
            return a.city < b.city ? -1 : 1;
          }
        });

        // Store that search has happened so we can show no results error if no results
        draft.submitted = true;

        break;

      case FORM_CLEAR:
        draft.form = {...initialState.form};
        draft.data.subcategories = [];
        draft.data.filteredCities = [];
        draft.results = [];
        draft.submitted = false;

        break;
    }

    console.log('results count:', draft.results.length);
  });

  return nextState;
}
